import axios from "axios";
import { 
  IInvoice, 
  IGuest, 
  IAdvert, 
  ISender, 
  IBooking, 
  ITextTemplate,
  IDashboardData,
  ITodayStats,
  IRevenueStats,
  IOccupancyStats,
  IGuestDashboardStats,
  IDashboardTask,
  IArrivalDeparture,
  IUnpaidInvoice,
  IBookingRequest,
  IUnregisteredGuest,
  IYearComparison,
  ITopAdvert
} from "../interfaces";
import { apiConfig, getFullApiUrl } from "../config/api";

// Service für TYPO3-spezifische Operationen
export class TYPO3Service {
  // Zentrale API-Konfiguration verwenden
  private static get baseURL(): string {
    return apiConfig.baseUrl;
  }

  private static get apiEndpoint(): string {
    return apiConfig.endpoints.restApi;
  }

  private static get extension(): string {
    return 'tx_v20adverts_booking';
  }

  private static get isDevelopment(): boolean {
    return apiConfig.isDevelopment;
  }
  
  // Helper-Methode zum Erstellen der API-URLs
  private static buildApiUrl(controller: string, action: string, params: Record<string, any> = {}): string {
    const searchParams = new URLSearchParams();
    
    // TYPO3 Extbase Parameter
    searchParams.append(`${TYPO3Service.extension}[controller]`, controller);
    searchParams.append(`${TYPO3Service.extension}[action]`, action);
    
    // Zusätzliche Parameter hinzufügen
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(`${TYPO3Service.extension}[${key}]`, String(value));
      }
    });

    return `${TYPO3Service.baseURL}${TYPO3Service.apiEndpoint}?${searchParams.toString()}`;
  }

  // Helper für alte API-Endpunkte (Fallback während Migration)
  private static buildLegacyApiUrl(endpoint: string, params: URLSearchParams): string {
    return `${TYPO3Service.baseURL}${endpoint}?${params.toString()}`;
  }

  /**
   * Lädt alle Rechnungen vom TYPO3 DocumentController
   */
  static async getInvoices(filters: any = {}) {
    try {
      const apiParams: Record<string, any> = {
        type: 'invoice'
      };
      
      // Filter hinzufügen
      if (filters.year) apiParams.year = filters.year;
      if (filters.month) apiParams.month = filters.month;
      if (filters.searchword) apiParams.searchword = filters.searchword;

      const apiUrl = TYPO3Service.buildApiUrl('Document', 'list', apiParams);
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Fehler beim Laden der Rechnungen:', error);
      throw error;
    }
  }

  /**
   * Lädt eine einzelne Rechnung
   */
  static async getInvoice(id: number) {
    try {
      const apiParams = { document: String(id) };
      const apiUrl = TYPO3Service.buildApiUrl('Document', 'show', apiParams);
      
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Laden der Rechnung ${id}:`, error);
      throw error;
    }
  }

  /**
   * Erstellt eine neue Rechnung
   */
  static async createInvoice(invoiceData: any) {
    if (this.isDevelopment) {
      console.log('Development Mode: Simuliere Rechnungserstellung', invoiceData);
      return { success: true, id: Date.now() };
    }

    try {
      const apiUrl = TYPO3Service.buildApiUrl('Document', 'create', invoiceData);
      const response = await axios.post(apiUrl, invoiceData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Fehler beim Erstellen der Rechnung:', error);
      throw error;
    }
  }

  /**
   * Lädt alle Adverts (Unterkünfte)
   */
  static async getAdverts() {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Advert', 'list');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.adverts || [];
    } catch (error) {
      console.error('Fehler beim Laden der Adverts:', error);
      return [];
    }
  }

  /**
   * Lädt ein einzelnes Advert
   */
  static async getAdvert(id: number) {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Advert', 'show', { id: String(id) });
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.advert || null;
    } catch (error) {
      console.error('Fehler beim Laden des Adverts:', error);
      return null;
    }
  }

  /**
   * Authentifizierung prüfen
   */
  static async checkAuth() {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Auth', 'check');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Fehler bei der Authentifizierung:', error);
      return { authenticated: false };
    }
  }

  /**
   * Lädt alle Gäste
   */
  static async getGuests(filters: any = {}) {
    try {
      const apiParams: Record<string, any> = {};
      
      if (filters.searchword) apiParams.searchword = filters.searchword;
      if (filters.limit) apiParams.limit = filters.limit;

      const apiUrl = TYPO3Service.buildApiUrl('Guest', 'list', apiParams);
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.guests || [];
    } catch (error) {
      console.error('Fehler beim Laden der Gäste:', error);
      return [];
    }
  }

  /**
   * Lädt einen einzelnen Gast
   */
  static async getGuest(id: number) {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Guest', 'show', { id: String(id) });
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.guest || null;
    } catch (error) {
      console.error(`Fehler beim Laden des Gastes ${id}:`, error);
      return null;
    }
  }

  /**
   * Erstellt einen neuen Gast
   */
  static async createGuest(guestData: IGuest) {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Guest', 'create', guestData);
      const response = await axios.post(apiUrl, guestData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Fehler beim Erstellen des Gastes:', error);
      throw error;
    }
  }

  /**
   * Aktualisiert einen Gast
   */
  static async updateGuest(id: number, guestData: Partial<IGuest>) {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Guest', 'update', { id: String(id), ...guestData });
      const response = await axios.put(apiUrl, guestData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error(`Fehler beim Aktualisieren des Gastes ${id}:`, error);
      throw error;
    }
  }

  /**
   * Lädt Buchungen für einen Gast
   */
  static async getBookingsForGuest(guestId: number) {
    try {
      const apiUrl = `${getFullApiUrl()}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=guest-bookings&tx_buchungsmanager_frontend[id]=${guestId}`;
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      const data = response.data;
      
      // Prüfe auf Fehler-Response (Objekt mit error-Property)
      if (data && typeof data === 'object' && data.error) {
        console.error(`API-Fehler beim Laden der Buchungen für Gast ${guestId}:`, data.error);
        return [];
      }
      
      // Stelle sicher, dass ein Array zurückgegeben wird
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error(`Fehler beim Laden der Buchungen für Gast ${guestId}:`, error);
      return [];
    }
  }

  /**
   * Lädt Rechnungen für einen Gast
   */
  static async getInvoicesForGuest(guestId: number) {
    try {
      const apiUrl = `${getFullApiUrl()}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=guest-invoices&tx_buchungsmanager_frontend[id]=${guestId}`;
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      const data = response.data;
      
      // Prüfe auf Fehler-Response (Objekt mit error-Property)
      if (data && typeof data === 'object' && data.error) {
        console.error(`API-Fehler beim Laden der Rechnungen für Gast ${guestId}:`, data.error);
        return [];
      }
      
      // Stelle sicher, dass ein Array zurückgegeben wird
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error(`Fehler beim Laden der Rechnungen für Gast ${guestId}:`, error);
      return [];
    }
  }

  /**
   * Lädt Angebote für einen Gast
   */
  static async getOffersForGuest(guestId: number) {
    try {
      const apiUrl = `${getFullApiUrl()}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=guest-offers&tx_buchungsmanager_frontend[id]=${guestId}`;
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      const data = response.data;
      
      // Prüfe auf Fehler-Response (Objekt mit error-Property)
      if (data && typeof data === 'object' && data.error) {
        console.error(`API-Fehler beim Laden der Angebote für Gast ${guestId}:`, data.error);
        return [];
      }
      
      // Stelle sicher, dass ein Array zurückgegeben wird
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error(`Fehler beim Laden der Angebote für Gast ${guestId}:`, error);
      return [];
    }
  }

  /**
   * Lädt Bewertungen für einen Gast
   */
  static async getRatingsForGuest(guestId: number) {
    try {
      const apiUrl = `${getFullApiUrl()}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=guest-ratings&tx_buchungsmanager_frontend[id]=${guestId}`;
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      const data = response.data;
      
      // Prüfe auf Fehler-Response (Objekt mit error-Property)
      if (data && typeof data === 'object' && data.error) {
        console.error(`API-Fehler beim Laden der Bewertungen für Gast ${guestId}:`, data.error);
        return [];
      }
      
      // Stelle sicher, dass ein Array zurückgegeben wird
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error(`Fehler beim Laden der Bewertungen für Gast ${guestId}:`, error);
      return [];
    }
  }

  /**
   * Lädt detaillierte Statistiken für einen Gast
   */
  static async getGuestDetailStatistics(guestId: number) {
    try {
      const apiUrl = `${getFullApiUrl()}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=guest-statistics&tx_buchungsmanager_frontend[id]=${guestId}`;
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data || {};
    } catch (error) {
      console.error(`Fehler beim Laden der Statistiken für Gast ${guestId}:`, error);
      return {};
    }
  }

  /**
   * Lädt Gäste-Statistiken
   */
  static async getGuestStatistics() {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Guest', 'statistics');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.statistics || {};
    } catch (error) {
      console.error('Fehler beim Laden der Gäste-Statistiken:', error);
      return {};
    }
  }

  /**
   * Lädt Absender
   */
  static async getSenders() {
    try {
      const apiUrl = this.buildApiUrl('Sender', 'list');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.senders || [];
    } catch (error) {
      console.error('Fehler beim Laden der Absender:', error);
      return [];
    }
  }

  /**
   * Lädt Textbausteine
   */
  static async getTextTemplates() {
    try {
      const apiUrl = this.buildApiUrl('Template', 'list');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.templates || [];
    } catch (error) {
      console.error('Fehler beim Laden der Textbausteine:', error);
      return [];
    }
  }

  /**
   * Aktualisiert den Zahlungsstatus einer Rechnung
   */
  static async updatePaymentStatus(invoiceId: number, status: string, amount?: number) {
    try {
      const apiUrl = this.buildApiUrl('Document', 'updatePaymentStatus', {
        id: String(invoiceId),
        status,
        amount: amount?.toString()
      });
      
      const response = await axios.post(apiUrl, { status, amount }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error(`Fehler beim Aktualisieren des Zahlungsstatus für Rechnung ${invoiceId}:`, error);
      throw error;
    }
  }

  /**
   * PDF Download einer Rechnung
   */
  static async downloadInvoicePDF(invoiceId: number) {
    try {
      const apiUrl = this.buildApiUrl('Document', 'downloadPDF', { id: String(invoiceId) });
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      return response.data;
    } catch (error) {
      console.error(`Fehler beim PDF Download für Rechnung ${invoiceId}:`, error);
      throw error;
    }
  }

  /**
   * Erneutes Senden einer Rechnung per E-Mail
   */
  static async resendInvoice(invoiceId: number, emailAddress?: string) {
    try {
      const apiUrl = this.buildApiUrl('Document', 'resendEmail', {
        id: String(invoiceId),
        email: emailAddress
      });
      
      const response = await axios.post(apiUrl, { emailAddress }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error(`Fehler beim Versenden der Rechnung ${invoiceId}:`, error);
      throw error;
    }
  }

  /**
   * Stornierung einer Rechnung
   */
  static async cancelInvoice(invoiceId: number, reason?: string) {
    try {
      const apiUrl = this.buildApiUrl('Document', 'cancel', {
        id: String(invoiceId),
        reason
      });
      
      const response = await axios.post(apiUrl, { reason }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error(`Fehler beim Stornieren der Rechnung ${invoiceId}:`, error);
      throw error;
    }
  }

  /**
   * Prüfung auf doppelte Rechnungsnummer
   */
  static async checkDuplicateInvoiceNumber(number: string, excludeId?: number) {
    try {
      const apiUrl = this.buildApiUrl('Document', 'checkDuplicate', {
        number,
        excludeId: excludeId?.toString()
      });
      
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.isDuplicate || false;
    } catch (error) {
      console.error(`Fehler beim Prüfen der Rechnungsnummer ${number}:`, error);
      return false;
    }
  }

  /**
   * Lädt Kalender-Daten
   */
  static async getCalendarData(filters: any = {}) {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Calendar', 'data', filters);
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Fehler beim Laden der Kalender-Daten:', error);
      throw error;
    }
  }

  /**
   * Lädt alle Buchungen
   */
  static async getBookings(filters: any = {}) {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Booking', 'list', filters);
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.bookings || [];
    } catch (error) {
      console.error('Fehler beim Laden der Buchungen:', error);
      throw error;
    }
  }

  // ========================================
  // DASHBOARD API METHODEN
  // ========================================

  /**
   * Lädt alle Dashboard-Daten
   */
  static async getDashboardData(): Promise<IDashboardData> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'getData');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      const data = response.data?.dashboard || {};
      
      // Stelle sicher, dass alle Arrays definiert sind
      return {
        ...data,
        tasks: data.tasks || [],
        arrivals: data.arrivals || [],
        departures: data.departures || [],
        unpaidInvoices: data.unpaidInvoices || [],
        bookingRequests: data.bookingRequests || [],
        unregisteredGuests: data.unregisteredGuests || [],
        topAdverts: data.topAdverts || []
      } as IDashboardData;
    } catch (error) {
      console.error('Fehler beim Laden der Dashboard-Daten:', error);
      // Rückgabe eines vollständigen leeren Dashboard-Objekts
      return {
        today: {} as ITodayStats,
        revenue: {} as any,
        occupancy: {} as any,
        guests: {} as any,
        tasks: [],
        arrivals: [],
        departures: [],
        unpaidInvoices: [],
        bookingRequests: [],
        unregisteredGuests: [],
        yearComparison: {} as any,
        topAdverts: []
      } as IDashboardData;
    }
  }

  /**
   * Lädt heutige Statistiken
   */
  static async getTodayStats(): Promise<ITodayStats> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'todayStats');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.stats || {} as ITodayStats;
    } catch (error) {
      console.error('Fehler beim Laden der Tagesstatistiken:', error);
      return {} as ITodayStats;
    }
  }

  /**
   * Lädt An- und Abreisen für heute
   */
  static async getTodayArrivals(): Promise<IArrivalDeparture[]> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'todayArrivals');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.arrivals || [];
    } catch (error) {
      console.error('Fehler beim Laden der heutigen Anreisen:', error);
      return [];
    }
  }

  /**
   * Lädt Abreisen für heute
   */
  static async getTodayDepartures(): Promise<IArrivalDeparture[]> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'todayDepartures');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.departures || [];
    } catch (error) {
      console.error('Fehler beim Laden der heutigen Abreisen:', error);
      return [];
    }
  }

  /**
   * Lädt unbezahlte Rechnungen
   */
  static async getUnpaidInvoices(): Promise<IUnpaidInvoice[]> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'unpaidInvoices');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.unpaidInvoices || [];
    } catch (error) {
      console.error('Fehler beim Laden der unbezahlten Rechnungen:', error);
      return [];
    }
  }

  /**
   * Lädt offene Buchungsanfragen
   */
  static async getBookingRequests(): Promise<IBookingRequest[]> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'bookingRequests');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.requests || [];
    } catch (error) {
      console.error('Fehler beim Laden der Buchungsanfragen:', error);
      return [];
    }
  }

  /**
   * Lädt nicht gemeldete Gäste
   */
  static async getUnregisteredGuests(): Promise<IUnregisteredGuest[]> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'unregisteredGuests');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.unregistered || [];
    } catch (error) {
      console.error('Fehler beim Laden der nicht gemeldeten Gäste:', error);
      return [];
    }
  }

  /**
   * Lädt Jahresvergleich
   */
  static async getYearComparison(): Promise<IYearComparison> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'yearComparison');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.comparison || {} as IYearComparison;
    } catch (error) {
      console.error('Fehler beim Laden des Jahresvergleichs:', error);
      return {} as IYearComparison;
    }
  }

  /**
   * Lädt Tagesaufgaben
   */
  static async getDashboardTasks(): Promise<IDashboardTask[]> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'tasks');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.tasks || [];
    } catch (error) {
      console.error('Fehler beim Laden der Dashboard-Aufgaben:', error);
      return [];
    }
  }

  /**
   * Markiert eine Aufgabe als erledigt
   */
  static async markTaskCompleted(taskId: number): Promise<boolean> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'markTaskCompleted', { taskId: String(taskId) });
      const response = await axios.post(apiUrl, { completed: true }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      return response.data?.success || false;
    } catch (error) {
      console.error(`Fehler beim Markieren der Aufgabe ${taskId}:`, error);
      return false;
    }
  }

  /**
   * Lädt Top-Adverts
   */
  static async getTopAdverts(): Promise<ITopAdvert[]> {
    try {
      const apiUrl = TYPO3Service.buildApiUrl('Dashboard', 'topAdverts');
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data?.topAdverts || [];
    } catch (error) {
      console.error('Fehler beim Laden der Top-Adverts:', error);
      return [];
    }
  }

  // Mock-Daten für Entwicklung und Fallbacks
  static getMockAdverts(): IAdvert[] {
    return [
      {
        id: 1,
        uid: 101,
        title: "Moderne Ferienwohnung am Stadtrand",
        description: "Schöne 2-Zimmer Wohnung mit Balkon",
        address: "Musterstraße 123",
        zip: "12345",
        city: "Berlin",
        country: "Deutschland",
        email: "info@advert1.de",
        phone: "+49 30 12345678",
        owner: {
          uid: 201,
          name: "Max Mustermann",
          company: "Mustermann Immobilien",
          address: "Geschäftsstraße 456",
          zip: "12345",
          city: "Berlin",
          email: "max@mustermann.de",
          phone: "+49 30 87654321",
        },
        invoice_text: "Vielen Dank für Ihren Aufenthalt in unserer Ferienwohnung.",
        offer_text: "Gerne bieten wir Ihnen unsere moderne Ferienwohnung an.",
        price_per_night: 85.00,
        max_guests: 4,
        min_stay: 2,
        status: "active",
        created_at: "2024-01-15T10:00:00Z",
        updated_at: "2024-01-20T14:30:00Z"
      },
      {
        id: 2,
        uid: 102,
        title: "Gemütliches Landhaus mit Garten",
        description: "Ruhige Lage mit großem Garten und Terrasse",
        address: "Landweg 789",
        zip: "54321",
        city: "München",
        country: "Deutschland",
        email: "info@landhaus.de",
        phone: "+49 89 98765432",
        owner: {
          uid: 202,
          name: "Anna Schmidt",
          company: "Schmidt Ferienvermietung",
          address: "Hauptstraße 321",
          zip: "54321",
          city: "München",
          email: "anna@schmidt.de",
          phone: "+49 89 12345678",
        },
        invoice_text: "Herzlichen Dank für Ihren Besuch in unserem Landhaus.",
        offer_text: "Unser gemütliches Landhaus erwartet Sie!",
        price_per_night: 120.00,
        max_guests: 6,
        min_stay: 3,
        status: "active",
        created_at: "2024-02-01T09:15:00Z",
        updated_at: "2024-02-05T16:45:00Z"
      }
    ];
  }

  static getMockGuests(): IGuest[] {
    return [
      {
        id: 1,
        name: 'Familie Schmidt',
        firstname: 'Hans',
        lastname: 'Schmidt',
        email: 'schmidt@example.de',
        phone: '+49 123 456789',
        address: 'Beispielweg 45',
        zip: '54321',
        city: 'Beispielstadt',
        country: 'Deutschland',
        total_bookings: 3,
        total_revenue: 1850.50,
        last_booking: '2024-12-15',
        notes: 'Stammgast, bevorzugt Meerblick'
      },
      {
        id: 2,
        name: 'Herr Müller',
        firstname: 'Thomas',
        lastname: 'Müller',
        email: 'mueller@example.de',
        phone: '+49 987 654321',
        address: 'Teststraße 99',
        zip: '98765',
        city: 'Testhausen',
        country: 'Deutschland',
        total_bookings: 1,
        total_revenue: 642.00,
        last_booking: '2024-11-20',
        notes: ''
      }
    ];
  }



  // Dashboard Mock-Daten
  static getMockDashboardData(): IDashboardData {
    const today = new Date().toISOString().split('T')[0];
    return {
      today: {} as ITodayStats,
      revenue: {
        today: 1250.00,
        thisWeek: 8750.00,
        thisMonth: 35200.00,
        thisYear: 185000.00,
        lastYear: 165000.00,
        weekGrowth: 12.5,
        monthGrowth: 8.3,
        yearGrowth: 12.1
      },
      occupancy: {
        today: 85.7,
        thisWeek: 78.2,
        thisMonth: 82.5,
        thisYear: 75.8,
        lastYear: 68.9,
        availableRooms: 3,
        totalRooms: 21
      },
      guests: {
        totalActive: 42,
        newThisMonth: 15,
        vipGuests: 8,
        repeatGuests: 28,
        averageStay: 3.2
      },
      tasks: [],
      arrivals: [],
      departures: [],
      unpaidInvoices: [],
      bookingRequests: [],
      unregisteredGuests: [],
      yearComparison: {} as IYearComparison,
      topAdverts: []
    };
  }

  static getMockTodayStats(): ITodayStats {
    const today = new Date().toISOString().split('T')[0];
    return {
      date: today,
      arrivals: 8,
      departures: 5,
      currentGuests: 42,
      occupancyRate: 85.7,
      revenue: 1250.00,
      unpaidInvoices: 3,
      openRequests: 7
    };
  }

  static getMockArrivals(): IArrivalDeparture[] {
    return [
      {
        id: 1,
        guestName: 'Familie Müller',
        roomNumber: '201',
        advertTitle: 'Moderne Ferienwohnung am Stadtrand',
        time: '15:00',
        nights: 5,
        guests: 4,
        status: 'confirmed',
        contact: '+49 123 456789',
        specialRequests: 'Kinderbett benötigt'
      },
      {
        id: 2,
        guestName: 'Herr Weber',
        roomNumber: '105',
        advertTitle: 'Gemütliches Landhaus mit Garten',
        time: '16:30',
        nights: 3,
        guests: 2,
        status: 'pending',
        contact: '+49 987 654321'
      },
      {
        id: 3,
        guestName: 'Familie Schmidt',
        roomNumber: '304',
        advertTitle: 'Apartment mit Meerblick',
        time: '18:00',
        nights: 7,
        guests: 3,
        status: 'confirmed',
        contact: '+49 555 123456',
        specialRequests: 'Späte Anreise'
      }
    ];
  }

  static getMockDepartures(): IArrivalDeparture[] {
    return [
      {
        id: 4,
        guestName: 'Familie Johnson',
        roomNumber: '102',
        advertTitle: 'Studio-Apartment Zentral',
        time: '11:00',
        nights: 4,
        guests: 2,
        status: 'confirmed',
        contact: '+49 333 777888'
      },
      {
        id: 5,
        guestName: 'Herr Klein',
        roomNumber: '203',
        advertTitle: 'Moderne Ferienwohnung am Stadtrand',
        time: '10:30',
        nights: 2,
        guests: 1,
        status: 'delayed',
        contact: '+49 444 999000',
        specialRequests: 'Checkout verlängert'
      }
    ];
  }

  static getMockUnpaidInvoices(): IUnpaidInvoice[] {
    const today = new Date();
    const dueDate1 = new Date(today);
    dueDate1.setDate(today.getDate() - 5);
    const dueDate2 = new Date(today);
    dueDate2.setDate(today.getDate() + 7);
    
    return [
      {
        id: 1,
        number: 'MM-2025-001',
        guestName: 'Familie Weber',
        amount: 850.00,
        dueDate: dueDate1.toISOString().split('T')[0],
        daysPastDue: 5,
        type: 'invoice'
      },
      {
        id: 2,
        number: 'MM-2025-003',
        guestName: 'Herr Klein',
        amount: 420.00,
        dueDate: dueDate2.toISOString().split('T')[0],
        daysPastDue: 0,
        type: 'invoice'
      },
      {
        id: 3,
        number: 'DEP-2025-005',
        guestName: 'Familie Schmidt',
        amount: 200.00,
        dueDate: today.toISOString().split('T')[0],
        daysPastDue: 0,
        type: 'deposit'
      }
    ];
  }

  static getMockBookingRequests(): IBookingRequest[] {
    const today = new Date();
    const checkIn1 = new Date(today);
    checkIn1.setDate(today.getDate() + 14);
    const checkOut1 = new Date(checkIn1);
    checkOut1.setDate(checkIn1.getDate() + 5);
    
    return [
      {
        id: 1,
        guestName: 'Familie Bauer',
        email: 'bauer@example.de',
        checkIn: checkIn1.toISOString().split('T')[0],
        checkOut: checkOut1.toISOString().split('T')[0],
        guests: 4,
        advertTitle: 'Moderne Ferienwohnung am Stadtrand',
        requestDate: today.toISOString().split('T')[0],
        status: 'new',
        estimatedValue: 950.00
      },
      {
        id: 2,
        guestName: 'Herr Martinez',
        email: 'martinez@example.com',
        checkIn: '2025-02-15',
        checkOut: '2025-02-18',
        guests: 2,
        advertTitle: 'Gemütliches Landhaus mit Garten',
        requestDate: '2025-01-14',
        status: 'viewed',
        estimatedValue: 720.00
      }
    ];
  }

  static getMockUnregisteredGuests(): IUnregisteredGuest[] {
    const today = new Date();
    const arrival1 = new Date(today);
    arrival1.setDate(today.getDate() - 2);
    
    return [
      {
        id: 1,
        name: 'Familie García',
        advertTitle: 'Apartment mit Meerblick',
        arrivalDate: arrival1.toISOString().split('T')[0],
        daysSinceArrival: 2,
        nationality: 'Spanien',
        documentsRequired: ['Personalausweis', 'Meldeschein']
      },
      {
        id: 2,
        name: 'Herr Novak',
        advertTitle: 'Studio-Apartment Zentral',
        arrivalDate: today.toISOString().split('T')[0],
        daysSinceArrival: 0,
        nationality: 'Tschechien',
        documentsRequired: ['Reisepass']
      }
    ];
  }

  static getMockYearComparison(): IYearComparison {
    return {
      revenue: {
        thisYear: 185000.00,
        lastYear: 165000.00,
        growth: 12.1
      },
      bookings: {
        thisYear: 680,
        lastYear: 620,
        growth: 9.7
      },
      guests: {
        thisYear: 1240,
        lastYear: 1150,
        growth: 7.8
      },
      occupancy: {
        thisYear: 75.8,
        lastYear: 68.9,
        growth: 10.0
      }
    };
  }

  static getMockDashboardTasks(): IDashboardTask[] {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    return [
      {
        id: 1,
        title: 'Zimmer 201 reinigen',
        description: 'Check-out um 11:00, neue Gäste um 15:00',
        priority: 'high',
        dueDate: today.toISOString().split('T')[0],
        type: 'cleaning',
        completed: false,
        assignedTo: 'Maria'
      },
      {
        id: 2,
        title: 'Heizung in Zimmer 105 prüfen',
        description: 'Gast hat kalte Räume gemeldet',
        priority: 'urgent',
        dueDate: today.toISOString().split('T')[0],
        type: 'maintenance',
        completed: false,
        assignedTo: 'Hans'
      },
      {
        id: 3,
        title: 'Familie Müller begrüßen',
        description: 'VIP-Gäste, Willkommenskorb vorbereiten',
        priority: 'medium',
        dueDate: today.toISOString().split('T')[0],
        type: 'guest',
        completed: false
      },
      {
        id: 4,
        title: 'Rechnung MM-2025-001 nachfassen',
        description: '5 Tage überfällig, Familie Weber kontaktieren',
        priority: 'high',
        dueDate: today.toISOString().split('T')[0],
        type: 'payment',
        completed: false
      },
      {
        id: 5,
        title: 'Wäsche für morgen vorbereiten',
        description: '3 Checkout und 5 Check-ins erwartet',
        priority: 'medium',
        dueDate: tomorrow.toISOString().split('T')[0],
        type: 'cleaning',
        completed: true,
        assignedTo: 'Maria'
      }
    ];
  }

  static getMockTopAdverts(): ITopAdvert[] {
    return [
      {
        id: 1,
        title: 'Moderne Ferienwohnung am Stadtrand',
        revenue: 28500.00,
        bookings: 95,
        occupancyRate: 88.5,
        averageRate: 125.50,
        rating: 4.8
      },
      {
        id: 2,
        title: 'Gemütliches Landhaus mit Garten',
        revenue: 35200.00,
        bookings: 78,
        occupancyRate: 82.3,
        averageRate: 155.00,
        rating: 4.9
      },
      {
        id: 3,
        title: 'Apartment mit Meerblick',
        revenue: 42100.00,
        bookings: 120,
        occupancyRate: 91.2,
        averageRate: 98.75,
        rating: 4.7
      }
    ];
  }
}

// Hook für React-Komponenten
export const useTypo3Api = () => {
  return {
    // Rechnungen
    getInvoices: TYPO3Service.getInvoices,
    getInvoice: TYPO3Service.getInvoice,
    createInvoice: TYPO3Service.createInvoice,
    updatePaymentStatus: TYPO3Service.updatePaymentStatus,
    downloadInvoicePDF: TYPO3Service.downloadInvoicePDF,
    resendInvoice: TYPO3Service.resendInvoice,
    cancelInvoice: TYPO3Service.cancelInvoice,
    checkDuplicateInvoiceNumber: TYPO3Service.checkDuplicateInvoiceNumber,
    
    // Adverts & Absender
    getAdverts: TYPO3Service.getAdverts,
    getAdvert: TYPO3Service.getAdvert,
    getSenders: TYPO3Service.getSenders,
    getTextTemplates: TYPO3Service.getTextTemplates,
    
    // Gäste
    getGuests: TYPO3Service.getGuests,
    getGuest: TYPO3Service.getGuest,
    createGuest: TYPO3Service.createGuest,
    updateGuest: TYPO3Service.updateGuest,
    getBookingsForGuest: TYPO3Service.getBookingsForGuest,
    getInvoicesForGuest: TYPO3Service.getInvoicesForGuest,
    getOffersForGuest: TYPO3Service.getOffersForGuest,
    getRatingsForGuest: TYPO3Service.getRatingsForGuest,
    getGuestDetailStatistics: TYPO3Service.getGuestDetailStatistics,
    getGuestStatistics: TYPO3Service.getGuestStatistics,
    
    // Buchungen & Kalender
    getBookings: TYPO3Service.getBookings,
    getCalendarData: TYPO3Service.getCalendarData,
    
    // Dashboard
    getDashboardData: TYPO3Service.getDashboardData,
    getTodayStats: TYPO3Service.getTodayStats,
    getTodayArrivals: TYPO3Service.getTodayArrivals,
    getTodayDepartures: TYPO3Service.getTodayDepartures,
    getUnpaidInvoices: TYPO3Service.getUnpaidInvoices,
    getBookingRequests: TYPO3Service.getBookingRequests,
    getUnregisteredGuests: TYPO3Service.getUnregisteredGuests,
    getYearComparison: TYPO3Service.getYearComparison,
    getDashboardTasks: TYPO3Service.getDashboardTasks,
    markTaskCompleted: TYPO3Service.markTaskCompleted,
    getTopAdverts: TYPO3Service.getTopAdverts,
    
    // Authentifizierung
    checkAuth: TYPO3Service.checkAuth,
  };
}; 